import type { OnDestroy, ComponentRef, Type, ViewContainerRef } from '@angular/core'
import { ApplicationRef, Injectable, Injector, inject, isSignal } from '@angular/core'
import type { AppComponent } from '../../../app.component'

@Injectable({
  providedIn: 'root'
})
export class DynamicRefService implements OnDestroy {
  private injector = inject(Injector)

  static instance: DynamicRefService

  constructor() {
    DynamicRefService.instance = this
  }

  /**
   * Clears the static reference when Angular destroys this service.
   */
  ngOnDestroy(): void {
    if (DynamicRefService.instance === this) {
      Reflect.deleteProperty(DynamicRefService, 'instance')
    }
  }

  /**
   * Resolves the application reference lazily to avoid a cyclic dependency.
   */
  private get appRef(): ApplicationRef {
    // Avoid cyclic dependency
    return this.injector.get(ApplicationRef)
  }

  /**
   * Finds the root app container used to mount overlay components.
   */
  private get appViewRef(): ViewContainerRef {
    // AppComponent should be the first registered component
    const appRef: ComponentRef<AppComponent> = this.appRef.components[0]

    if (!appRef) {
      throw new Error('Cannot find AppComponent ComponentRef')
    }

    const appComp: AppComponent = appRef.instance

    // Validate the overlay host without importing the app root at runtime.
    if (!isSignal(appComp.overlayViewRef) || !appComp.viewRef) {
      throw new Error('Found incorrectly typed AppComponent')
    }

    return appComp.overlayViewRef() ?? appComp.viewRef
  }

  /**
   * Creates a component in the root overlay container and returns its reference and instance.
   * @param {Type<T>} type the generic type of the Component to be created
   * @returns {[ ComponentRef<T>, T ]} the ComponentRef and Component that were created dynamically
   * @template T
   */
  public create<T>(type: Type<T>): [ComponentRef<T>, T] {
    const ref: ComponentRef<T> = this.appViewRef.createComponent(type, {
      injector: this.injector
    })

    if (!ref) {
      throw new Error(`Cannot create ComponentRef of Type '${type.name}`)
    }

    return [ref, ref.instance]
  }
}
